import React, { useMemo, useState } from 'react';
import {Box, Typography, Grid, Chip, Card, CardContent, Button} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useTheme, alpha } from '@mui/material/styles';
import { tokens } from '../../theme';
import { TitleScreen, SubTitleScreen } from '../../SystemDesign/Styles';
import MuiFullScreenDialog from '../../SystemDesign/MuiFullScreenDialog';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ListView from '../../SystemDesign/ListViewV2';
import ListViewMenuActions from '../../SystemDesign/ListViewMenuActions';
import UpdateRequirementSkills from './UpdateRequirementSkills';
import DeleteRequirementsSkill from './DeleteRequirementsSkill';
import { AddRequirementForm } from './AddRequirementsSkills';

// Helpers
const getLevelName = (level) => {
  switch (Number(level)) {
    case 1:
      return '1 - Awareness';
    case 2:
      return '2 - Skilled Application';
    case 3:
      return '3 - Management';
    default:
      return String(level ?? '—');
  }
};

// Map level (number or label string) to consistent numeric value 1..3
const getLevelNumber = (level) => {
  const n = Number(level);
  if (!Number.isNaN(n) && n >= 1 && n <= 3) return n;
  const s = String(level || '').toLowerCase();
  if (s.includes('awareness')) return 1;
  if (s.includes('skilled')) return 2;
  if (s.includes('management')) return 3;
  return 0;
};


const SkillRequirementList = ({
  open = false,
  onClose = () => {},
  poste = null,
  requirements = [],
  onEdit,
  onDelete,
  rightFormProps = {},
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeReq, setActiveReq] = useState(null);

  const count = useMemo(() => requirements?.length || 0, [requirements]);

  const handleMenuOpen = (event, req) => {
    setActiveReq(req);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const handleEdit = () => {
    handleMenuClose();
    if (onEdit && activeReq) onEdit(activeReq);
  };

  const handleDelete = () => {
    handleMenuClose();
    if (onDelete && activeReq?.id) onDelete(activeReq.id);
  };

  const [selectedCount, setSelectedCount] = React.useState(0);
  const formRef = React.useRef(null);

  // Row actions for requirements
  const actionsList = React.useMemo(() => [
    { component: UpdateRequirementSkills, key: 'update' },
    { component: DeleteRequirementsSkill, key: 'delete' },
  ], []);

  const content = (
    <Box>
     

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' }, gap: 3, height: '70vh' }}>
        {/* Left: requirements list via ListViewV2 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            Currently Required Skills ({count})
          </Typography>

          <Card sx={{ flex: 1, overflow: 'hidden' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 1 }}>
                <ListView
                  dispatchData={requirements}
                  columns={[
                    {
                      id: 'skill',
                      accessorKey: 'skill',
                      header: 'Skill',
                      cell: (info) => {
                        const skill = info.getValue();
                        return (
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>{skill?.name || '—'}</Typography>
                          </Box>
                        );
                      },
                      enableColumnFilter: false,
                      enableSorting: false,
                    },
                    {
                      id: 'required_level',
                      accessorKey: 'required_level',
                      header: 'Level',
                      cell: (info) => {
                        const lvlNum = getLevelNumber(info.getValue());
                        const bg = lvlNum === 1
                          ? alpha(colors.skyBlue[400], 0.1)
                          : lvlNum === 2
                            ? alpha(colors.Orange[400], 0.1)
                            : alpha(colors.redAccent[400], 0.1);
                        const border = lvlNum === 1
                          ? `1px solid ${alpha(colors.skyBlue[400], 0.2)}`
                          : lvlNum === 2
                            ? `1px solid ${alpha(colors.Orange[400], 0.2)}`
                            : `1px solid ${alpha(colors.redAccent[400], 0.2)}`;
                        const textColor = colors.grey[800];
                        return (
                          <Chip
                            size="small"
                            label={getLevelName(info.getValue())}
                            sx={{
                              backgroundColor: bg,
                              border,
                              color: textColor,
                              fontWeight: 600,
                            }}
                            variant="outlined"
                          />
                        );
                      },
                      enableColumnFilter: false,
                      enableSorting: false,
                    },
                    {
                      id: 'score',
                      header: 'Score',
                      cell: ({ row }) => (
                        <Typography variant="body2">{(row.original?.min_score ?? 1)} - {(row.original?.max_score ?? 3)}</Typography>
                      ),
                      enableColumnFilter: false,
                      enableSorting: false,
                    },
                    
                    // Actions
                    {
                      id: 'actions',
                      header: '',
                      size: 100,
                      enableColumnFilter: false,
                      cell: ({ row }) => (
                        <ListViewMenuActions>
                          {actionsList.map(({ component: Component, key }) => (
                            <Component
                              key={key}
                              row={row}
                              actionKey={key}
                              onUpdated={rightFormProps?.onAdded}
                            />
                          ))}
                        </ListViewMenuActions>
                      ),
                    }
                  ]}
                  tableName={'requirements'}
                  columnFilters={[]}
                  setColumnFilters={() => {}}
                  applyFilters={() => {}}
                  pagination={{ currentPage: 1, rowsPerPage: requirements?.length || 10, total: requirements?.length || 0, totalPages: 1 }}
                  setQueryParams={() => {}}
                  queryParams={{}}
                  showColumnManager={false}
                  existingFilters={false}
                />
                {count === 0 && (
                  <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                    <TrackChangesIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography>No required skills for this position</Typography>
                    <Typography variant="caption">Add required skills using the form on the right</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Right: add requirement form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddCircleOutlineIcon fontSize="small" color="primary" />
            Add New Skills
          </Typography>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ maxHeight: '60vh', overflowY: 'auto', p: 2 }}>
              <AddRequirementForm
                ref={formRef}
                poste={poste}
                onAdded={rightFormProps.onAdded}
                existingRequirements={requirements}
                showHeader={false}
                showFooter={false}
                onSelectedChange={(n) => setSelectedCount(n)}
              />
            </CardContent>
          </Card>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Button
              variant="contained"
              disabled={!poste || selectedCount === 0}
              onClick={() => formRef.current && formRef.current.save && formRef.current.save()}
            >
              Save Skills ({selectedCount})
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <MuiFullScreenDialog
      open={open}
      onClose={onClose}
      icon={<TrackChangesIcon />}
      title={`Required Skills for position ${poste?.name ? ` - ${poste.name}` : ''}`}
      subtitle={'Manage required skills and their level for this position'}
      content={content}
      maxWidth="xl"
    />
  );
};

export default SkillRequirementList;